import { SyntheticEvent, useRef, useState } from "react";
import { iosSetBackgroundColor, isIOSApp } from "../../tools/ios";
import AppCarousel, { type AppCarouselHandle } from "../app/AppCarousel";
import ResponsiveImage, {
  type ResponsiveImageSizes,
} from "../app/ResponsiveImage";
import ImageViewer from "./ImageViewer";

// A component that combines a carousel and an image viewer

interface CarouselImage {
  url: string;
  path?: string;
  name?: string | null;
  legend?: string;
  sizes?: ResponsiveImageSizes | null;
}

export default function ImageCarouselViewer({
  images,
  onIndexChange = () => null,
}: {
  images: CarouselImage[];
  onIndexChange?: (index: number) => void;
}) {
  const [isImageViewerOpen, setImageViewerOpen] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);
  const carouselRef = useRef<AppCarouselHandle>(null);

  return (
    <>
      <div
        onClick={(e: SyntheticEvent) => {
          e.stopPropagation();
          setImageViewerOpen(true);
          if (isIOSApp()) iosSetBackgroundColor("#191919");
        }}
        onTouchStart={(e) => {
          e.stopPropagation();
        }}
        onTouchMove={(e) => {
          e.stopPropagation();
        }}
        onTouchEnd={(e) => {
          e.stopPropagation();
        }}
      >
        <AppCarousel
          slideGutter="sm"
          ref={carouselRef}
          onIndexChange={(index) => {
            setImageIndex(index);
            onIndexChange(index);
          }}
        >
          {images.map((image, index) => (
            <div key={index} className="relative h-[200px] w-full">
              <ResponsiveImage
                className="absolute inset-0"
                image={{
                  url: image.url,
                  path: image.path ?? "",
                  name: image.name,
                  legend: image.legend,
                  sizes: image.sizes,
                }}
                style={{ objectFit: "cover" }}
              />
            </div>
          ))}
        </AppCarousel>
      </div>

      <ImageViewer
        open={isImageViewerOpen}
        imgs={images}
        imageIndex={imageIndex}
        updateIndex={(index) => {
          setImageIndex(index);
          const carousel = carouselRef.current;
          if (carousel === null)
            throw new Error(
              "should not happen: figure carousel is not mounted"
            );
          // The viewer covers the carousel: jump without animation.
          carousel.scrollToIndex(index, "instant");
        }}
        onClose={() => {
          setImageViewerOpen(false);
        }}
      />
    </>
  );
}
